import { useState, useEffect, useCallback } from 'react';
import { Save, Loader2, Mic, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSaveInterviewResponse } from '@/hooks/useTeamProfileInterview';
import { useVoiceRecording } from '@/hooks/useVoiceRecording';
import type { InterviewQuestion, InterviewResponse } from '@/types/team-profiles';

interface InterviewQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  question: InterviewQuestion;
  profileId: string;
  existingResponse?: InterviewResponse;
}

export function InterviewQuestionModal({
  isOpen,
  onClose,
  question,
  profileId,
  existingResponse,
}: InterviewQuestionModalProps) {
  const [responseText, setResponseText] = useState(existingResponse?.response_text || '');
  const saveResponse = useSaveInterviewResponse();

  // Reset text when question changes
  useEffect(() => {
    setResponseText(existingResponse?.response_text || '');
  }, [existingResponse, question.id]);

  // Voice recording — transcript appends to textarea
  const handleTranscript = useCallback((transcript: string) => {
    setResponseText((prev) => (prev ? prev + '\n' + transcript : transcript));
  }, []);

  const { isRecording, isTranscribing, formattedTime, toggleRecording } = useVoiceRecording({
    maxDurationSeconds: 300,
    onTranscriptReady: handleTranscript,
  });

  const handleSave = async () => {
    if (!responseText.trim()) return;

    await saveResponse.mutateAsync({
      profileId,
      questionId: question.id,
      questionText: question.text,
      responseText: responseText.trim(),
      responseType: 'text',
    });

    onClose();
  };

  const hasChanges = responseText.trim() !== (existingResponse?.response_text || '');
  const canSave = responseText.trim().length > 0 && hasChanges;
  const isDisabled = isRecording || isTranscribing || saveResponse.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <span className="text-2xl">{question.emoji}</span>
            <span>{question.text}</span>
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            {question.subtext}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 py-4 overflow-y-auto">
          {/* Response Input with Recording */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="response">Your Response</Label>
              <div className="flex items-center gap-2">
                {isTranscribing && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Transcribing...
                  </span>
                )}
                {isRecording ? (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={toggleRecording}
                    className="gap-1.5 h-7"
                  >
                    <Square className="h-3 w-3" />
                    Stop ({formattedTime})
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={toggleRecording}
                    disabled={isDisabled}
                    className="gap-1.5 h-7"
                  >
                    <Mic className="h-3 w-3" />
                    Record Voice
                  </Button>
                )}
              </div>
            </div>

            {/* Recording Status */}
            {isRecording && (
              <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs">
                <span className="animate-pulse text-destructive">●</span>
                <span className="text-destructive">Recording... Speak your response</span>
              </div>
            )}

            <Textarea
              id="response"
              placeholder={
                isRecording
                  ? "Listening... click Stop when done"
                  : "Type your response here, or click 'Record Voice' to speak your answer..."
              }
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              className={`min-h-[200px] resize-none ${isRecording ? 'border-red-300 bg-red-50/50' : ''}`}
              disabled={isDisabled}
            />
            <div className="text-xs text-muted-foreground text-right">
              {responseText.length} characters
            </div>
          </div>

          {/* Tips */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
            <div className="font-medium text-blue-900">Tips for a great response:</div>
            <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
              <li>Be specific with examples from your experience</li>
              <li>Share your unique perspective and insights</li>
              <li>Don't worry about perfect grammar — authenticity matters more</li>
              <li>Think about what makes your approach different</li>
            </ul>
          </div>

          {/* Previous Response */}
          {existingResponse && existingResponse.answered_at && (
            <div className="text-sm text-muted-foreground">
              Last answered: {new Date(existingResponse.answered_at).toLocaleString()}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave || saveResponse.isPending}
          >
            {saveResponse.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Response
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
